<template>
  <main class="d-flex flex-column flex-grow-1 align-items-center justify-content-center bg-primary">
    <section class="container py-3 py-sm-4 d-flex flex-grow-1 align-items-lg-center flex-lg-grow-0 position-relative">
      <div class="col-12 col-dialog px-0 mx-auto mt-5 mt-md-0 position-static">
        <div class="bg-lg-white text-white text-lg-body rounded p-md-4 p-lg-5">
          <logo class="mx-auto d-none d-lg-block mb-lg-5" />
          <logo-white class="mx-auto d-block d-lg-none mb-5" />
          <div class="alert alert-warning" v-if="maintenance">
            <strong>Scheduled Maintenance.</strong> We're currently performing maintenance. Please check back soon.
          </div>
          <VueSlideToggle :open="!forgot" tag="div" :duration="500" class="mb-lg-0 px-lg-1">
            <form class="needs-validation pt-5 pt-lg-0" @submit.prevent="login" novalidate>
              <h1 class="h3 mb-3 mb-sm-4 sr-only">Sign in to Campdraft One</h1>

              <div class="form-group">
                <label for="email">Email Address</label>
                <input type="email" class="form-control" id="email" maxlength="255" required v-model="email">
                <div class="invalid-tooltip">Please include your Email Address.</div>
              </div>

              <div class="form-group mb-4 mb-lg-3">
                <label for="password">Password</label>
                <input type="password" :class="['form-control', status === 'invalid_credentials' ? 'is-invalid' : '']" id="password" maxlength="30" required v-model="password.password">
                <div class="invalid-tooltip">{{ status === 'invalid_credentials' ? 'Please check your password.' : 'Please include your Password.' }}</div>
              </div>

              <button type="submit" :class="['btn btn-block mb-4 btn-primary-dark']" :disabled="showCaptcha && !captchaReady" @click="handleSubmit">
                {{ !processing[0] && !(showCaptcha && !captchaReady) ? 'Sign In' : ''}}
                <div v-if="processing[0] || (showCaptcha && !captchaReady)" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              </button>

              <div
                ref="recaptcha"
                class="mb-4 mb-lg-3 m-auto"
                style="width: 304px;"
                v-if="showCaptcha">
              </div>

              <div class="alert alert-info" v-if="status === 'requires_verification'">Please check your registration email to verify your account.</div>
              <div class="alert alert-warning" v-else-if="status === 'invalid_credentials'">Please check your login details and try again. <div v-text="message"></div></div>
              <div class="alert alert-warning" v-else-if="status === 'invalid_captcha'" v-text="message"></div>
              <div class="alert alert-danger" v-else-if="status === 'too_many_attempts'" v-text="message"></div>
              <div class="alert alert-danger" v-else-if="status === 'onhold'">Oops! Looks like your membership is on hold, please contact the ACA office on (07) 4622 3110.</div>
              <div class="alert alert-warning" v-else-if="status === 'maintenance'">We're currently performing maintenance. Please check back soon.</div>
              <div class="alert alert-danger" v-else-if="status === 'undefined_error' || status === 'token_error'">There was a server error. Please try again later.</div>
              <div class="alert alert-info" v-else-if="status === 'reset_request'">Your password reset request has been sent.</div>
            </form>
          </VueSlideToggle>
          <VueSlideToggle :open="!forgot" tag="div" :duration="500" class="d-sm-flex justify-content-sm-between px-lg-1">
            <a href class="d-block mb-2 mb-sm-0" @click.prevent="resetForm();forgot = !forgot">Forgot your password?</a>
            <a href class="d-block mb-2 mb-sm-0 text-sm-right" @click.prevent="registerModal">Need to create a new account?</a>
          </VueSlideToggle>
          <VueSlideToggle :open="forgot" tag="div" :duration="500" class="mb-lg-0 px-lg-1">
            <form class="needs-validation" @submit.prevent="recoverPassword" novalidate>
              <h1 class="h3 mb-4 pb-2 pb-lg-0 font-weight-normal text-center">Forgot your password?</h1>
              <div class="form-group mb-4 mb-lg-3">
                <label for="forgotEmail">Email Address</label>
                <input type="email" class="form-control" id="forgotEmail" required v-model="email">
                <div class="invalid-tooltip">Please include your Email Address.</div>
              </div>
              <button type="submit" :class="['btn btn-block mb-4', processing[1] ? 'btn-primary-dark' : 'btn-primary-dark']" @click="handleSubmit">
                {{ !processing[1] ? 'Recover Password' : ''}}
                <div v-if="processing[1]" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              </button>

              <div class="alert alert-success" v-if="status === 'reset_request' || status === 'password_sent'">Your password request has been sent. You will receive an email with instructions on how to continue.</div>
              <div class="alert alert-warning" v-if="status === 'invalid_credentials'">An account wasn't found with those details. Please check the email address supplied.</div>
              <div class="alert alert-danger" v-if="status === 'onhold'">Oops! Looks like your membership is on hold, please contact the ACA office on (07) 4622 3110.</div>
              <div class="alert alert-danger" v-if="status === 'password_cannot_send'">Admins and Secretary Only cannot request a password reset. Please ask another admin to change your password on your behalf</div>
              <div class="alert alert-danger" v-if="status === 'undefined_error'">There was a server error. Please try again later.</div>
            </form>
          </VueSlideToggle>
          <VueSlideToggle :open="forgot" tag="div" :duration="500">
            Back to <a href @click.prevent="resetForm();forgot = !forgot">Sign in</a>.
          </VueSlideToggle>
        </div>
      </div>

      <modal v-if="showRegisterModal" @close="showRegisterModal = false">
        <div slot="body">
          <div class="alert alert-warning">
            To create a Campdraft One account please contact the office on  07 4622 3110, Monday to Friday 8.30am - 4pm
          </div>
        </div>
      </modal>

    </section>
  </main>
</template>

<script>
import { storeSuperSecretaryFlags } from '@/services/actingCommittee'
import HANDLESUBMIT from '@/mixins/formValidation.js'
import logo from '@/assets/logo'
import logoWhite from '@/assets/logoWhite'
import axios from 'axios'
import { VueSlideToggle } from 'vue-slide-toggle'
import modal from '@/components/Modal'
/* global grecaptcha */

export default {
  props: ['newStatus'],
  name: 'Login',
  components: {
    logo,
    'logo-white': logoWhite,
    VueSlideToggle,
    modal
  },
  mixins: [HANDLESUBMIT],
  data() {
    return {
      email: this.$route.query.email,
      forgot: false,
      status: this.newStatus || '',
      message: '',
      showRegisterModal: false,
      processing: [false, false],
      showCaptcha: process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'staging',
      captchaReady: false,
      captchaWidgetId: null,
      maintenance: false
    }
  },
  mounted() {
    axios.get(`${this.$api}/v2/maintenance-status`).then((response) => {
      this.maintenance = !!response.data.data.maintenance
    }).catch(() => {})

    // When you navigate to the login page.
    // If logged in, invalidate the auth token.
    if (localStorage.getItem('bearer')) {
      axios.post(`${this.$api}/auth/logout`)
      delete axios.defaults.headers.common.Authorization
    }
    // Remove all "session" items.
    localStorage.clear()
    // Reset Vuex if you were just logged in
    this.$store.commit('updateChildMember', '')
    this.$store.commit('updateViewedMember', '')

    if (this.showCaptcha) {
      const checkReady = setInterval(() => {
        if (typeof grecaptcha !== 'undefined' && typeof grecaptcha.render === 'function') {
          this.captchaWidgetId = grecaptcha.render(this.$refs.recaptcha, { sitekey: this.$captchaSiteKey })
          this.captchaReady = true
          clearInterval(checkReady)
        }
      }, 100)
    }
  },
  methods: {
    login(event) {
      event.preventDefault()

      if (!this.email || !this.password) {
        return
      }

      const token = this.showCaptcha && typeof grecaptcha !== 'undefined' && typeof grecaptcha.getResponse === 'function' ? grecaptcha.getResponse(this.captchaWidgetId) : null

      if (this.showCaptcha && !token) {
        this.status = 'invalid_captcha'
        this.message = 'Please complete the reCAPTCHA.'
        return
      }

      this.processing.splice(0, 1, true)

      axios.post(`${this.$api}/auth/login`, {
        email: this.email,
        password: this.password.password,
        recaptcha: token
      }).then((response) => {
        this.processing.splice(0, 1, false)
        // If we had an auth token in response
        if (response.data.token) {
          // eslint-disable-next-line
          // console.log(response.data)
          localStorage.setItem('bearer', response.data.token)
          // Attach the token as future headers.
          axios.defaults.headers.common.Authorization = `Bearer ${response.data.token}`
          localStorage.setItem('first_name', response.data.first_name)
          localStorage.setItem('last_name', response.data.last_name)
          localStorage.setItem('role', response.data.role)
          localStorage.setItem('userID', response.data.id)
          localStorage.setItem('is_onboarded', response.data.is_onboarded)
          storeSuperSecretaryFlags(response.data, { resetSelection: true })

          // Redirect them to the dashboard
          if (response.data.is_onboarded) {
            this.$router.push({ name: 'Dashboard' })
          } else {
            localStorage.setItem('userDetails', JSON.stringify(response.data.userDetails))
            this.$router.push({ name: 'Login Once', params: { userDetails: response.data.userDetails } })
          }
        // Handle errors and display approptiate messages
        } else {
          if (this.showCaptcha) {
            grecaptcha.reset(this.captchaWidgetId)
          }
          if (response.data.status === 'maintenance') {
            this.maintenance = true
          }
          this.status = response.data.status
          this.message = response.data.message
        }
      }).catch((error) => {
        this.processing.splice(0, 1, false)
        this.status = 'undefined_error'
        // eslint-disable-next-line
        console.log(error)
      })
    },
    recoverPassword() {
      if (!this.email) {
        return
      }

      this.processing.splice(1, 1, true)

      axios.post(`${this.$api}/auth/forgot`, {
        email: this.email
      }).then((response) => {
        this.processing.splice(1, 1, false)
        this.status = response.data.status
      }).catch((error) => {
        this.processing.splice(1, 1, false)
        this.status = 'undefined_error'
        // eslint-disable-next-line
        console.log(error)
      })
    },
    registerModal() {
      this.showRegisterModal = true
    }
  }
}
</script>

<style lang="scss" scoped>
.col-dialog {
  flex: 0 1 460px;
  max-width: 460px;
}

.align-bottom-mob {

  @media (max-width: 1023px) {
    position: absolute;
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
  }
}
</style>



// WEBPACK FOOTER //
// src/views/Account/Login.vue